import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Car, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { inr, todayISO, calcDuty, CITIES, DUTY_STATUSES, DUTY_TYPES, VEHICLE_TYPES } from "@/lib/format";
import { PageHeader, Panel, StatusBadge } from "@/components/common";
import { FieldGrid } from "@/components/Field";
import { useAuth } from "@/context/AuthContext";

const n = (key, label, extra = {}) => ({ key, label, type: "number", ...extra });

export default function DutyForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const admin = useAuth().user?.role === "admin";
  const [d, setD] = useState({ duty_date: todayISO(), start_date: todayISO(), status: "Scheduled", vehicle_source: "own", city: CITIES[0] });
  const [companies, setCompanies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [owners, setOwners] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
    api.get("/owners").then((r) => setOwners(r.data));
    if (id) api.get(`/duties/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Duty not found"));
  }, [id]);

  const change = (k, v) =>
    setD((s) => {
      const next = { ...s, [k]: v };
      if (k === "duty_type") {
        const t = DUTY_TYPES.find((x) => x.label === v);
        if (t && t.km) Object.assign(next, { package_km: t.km, package_hours: t.hrs, package_name: v });
        else next.package_name = v;
      }
      if (k === "duty_date" && (!s.start_date || s.start_date === s.duty_date)) next.start_date = v;
      if (k === "vehicle_id") {
        const veh = vehicles.find((x) => x.id === v);
        if (veh) Object.assign(next, { vehicle_number: veh.vehicle_number, driver_name: s.driver_name || veh.driver_name || "", driver_mobile: s.driver_mobile || veh.driver_mobile || "", vehicle_type: s.vehicle_type || veh.vehicle_type || "" });
      }
      if (k === "company_id") {
        const c = companies.find((x) => x.id === v);
        if (c && !s.id) Object.assign(next, { extra_km_rate: s.extra_km_rate || c.extra_km_rate || "", extra_hr_rate: s.extra_hr_rate || c.extra_hr_rate || "", package_rate: s.package_rate || c.default_package_rate || "" });
      }
      if (k === "owner_id") {
        const o = owners.find((x) => x.id === v);
        if (o && !s.owner_rate) Object.assign(next, { owner_rate: o.rate_8hr_80km || "", owner_extra_km_rate: o.extra_km_rate || "" });
      }
      return next;
    });

  const c = useMemo(() => calcDuty(d), [d]);
  const market = d.vehicle_source === "market";

  const save = async () => {
    if (!d.company_id) return toast.error("Please select a company");
    setSaving(true);
    try {
      const r = id ? await api.put(`/duties/${id}`, d) : await api.post("/duties", d);
      toast.success(id ? "Duty updated" : `Duty ${r.data.duty_no} created`);
      nav("/duties");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const booking = [
    { key: "duty_date", label: "Duty Date", type: "date", required: true },
    { key: "company_id", label: "Company / Client", type: "select", required: true, options: companies.map((x) => ({ value: x.id, label: x.name })), placeholder: companies.length ? "Select company" : "Add a company first" },
    { key: "booking_ref", label: "Booking / Duty Slip Ref", placeholder: "e.g. ECI-458921" },
    { key: "duty_type", label: "Duty Type", type: "select", options: DUTY_TYPES.map((t) => t.label) },
    { key: "city", label: "City", type: "select", options: CITIES },
    { key: "status", label: "Status", type: "select", options: DUTY_STATUSES.filter((s) => s !== "Billed" || d.status === "Billed") },
    { key: "guest_name", label: "Guest Name" },
    { key: "guest_mobile", label: "Guest Mobile", type: "tel" },
    { key: "reporting_time", label: "Reporting Time", type: "time" },
    { key: "pickup_location", label: "Pickup Location", span: 2 },
    { key: "drop_location", label: "Drop Location" },
  ];
  const vehicle = market
    ? [
        { key: "owner_id", label: "Vehicle Owner", type: "select", options: owners.map((o) => ({ value: o.id, label: `${o.name}${o.city ? ` · ${o.city}` : ""}` })), placeholder: owners.length ? "Select owner" : "Add an owner first" },
        { key: "vehicle_number", label: "Vehicle Number", placeholder: "MH-20-XX-0000" },
        { key: "vehicle_type", label: "Vehicle Type", type: "select", options: VEHICLE_TYPES },
        { key: "driver_name", label: "Driver Name" },
        { key: "driver_mobile", label: "Driver Mobile", type: "tel" },
      ]
    : [
        { key: "vehicle_id", label: "Own Vehicle", type: "select", options: vehicles.map((v) => ({ value: v.id, label: `${v.vehicle_number} · ${v.model || ""}` })), placeholder: vehicles.length ? "Select vehicle" : "Add a vehicle first" },
        { key: "vehicle_type", label: "Vehicle Type", type: "select", options: VEHICLE_TYPES },
        { key: "driver_name", label: "Driver Name" },
        { key: "driver_mobile", label: "Driver Mobile", type: "tel" },
      ];
  const trip = [
    { key: "start_date", label: "Start Date", type: "date" },
    { key: "start_time", label: "Start Time", type: "time" },
    { key: "end_date", label: "End Date", type: "date" },
    { key: "end_time", label: "End Time", type: "time" },
    n("start_km", "Opening KM"),
    n("end_km", "Closing KM"),
    n("total_hours_manual", "Total Hours (override)", { placeholder: "Auto from times" }),
  ];
  const billing = [
    { key: "package_name", label: "Package Name", placeholder: "8hr / 80km" },
    n("package_km", "Package KM"),
    n("package_hours", "Package Hours"),
    n("package_rate", "Package Rate (₹)"),
    n("extra_km_rate", "Extra KM Rate (₹/km)"),
    n("extra_hr_rate", "Extra Hour Rate (₹/hr)"),
    n("driver_allowance", "Driver Allowance (₹)"),
    n("night_charges", "Night Charges (₹)"),
    n("other_charges", "Other Charges (₹)"),
    n("toll", "Toll (₹)"),
    n("parking", "Parking (₹)"),
    n("state_tax", "State Tax / Permit (₹)"),
  ];
  const cost = market
    ? [n("owner_rate", "Owner Package Rate (₹)"), n("owner_extra_km_rate", "Owner Extra KM Rate"), n("owner_extra_hr_rate", "Owner Extra Hr Rate"), n("owner_paid", "Paid to Owner (₹)")]
    : [n("fuel_cost", "Fuel Cost (₹)"), n("driver_cost", "Driver Salary / Bata (₹)"), n("other_expense", "Other Expense (₹)")];

  const Row = ({ l, v, strong, tone, testid }) => (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{l}</span>
      <span className={`num ${strong ? "font-bold text-base" : "font-medium"} ${tone || ""}`} data-testid={testid}>{v}</span>
    </div>
  );

  return (
    <div data-testid="duty-form-page">
      <Link to="/duties" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-duties"><ArrowLeft className="h-4 w-4 mr-1" /> Duties</Link>
      <PageHeader eyebrow={id ? d.duty_no : "New entry"} title={id ? "Edit Duty" : "New Duty"}
        subtitle={d.invoice_no ? `Billed on invoice ${d.invoice_no}` : "Fill booking, vehicle, trip and billing details. Totals calculate live."}
        actions={<Button onClick={save} disabled={saving} className="h-11 sm:h-10" data-testid="duty-save-btn"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving..." : "Save Duty"}</Button>} />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <Panel title="1 · Booking Details"><FieldGrid fields={booking} data={d} onChange={change} testPrefix="duty" /></Panel>
          <Panel title="2 · Vehicle & Driver">
            <div className="grid grid-cols-2 gap-2 mb-5 max-w-md">
              {[["own", "Own Vehicle", Car], ["market", "Market Vehicle", Users]].map(([v, l, I]) => (
                <button key={v} type="button" onClick={() => change("vehicle_source", v)} data-testid={`source-${v}-btn`}
                  className={`h-11 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${d.vehicle_source === v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>
                  <I className="h-4 w-4" /> {l}
                </button>
              ))}
            </div>
            <FieldGrid fields={vehicle} data={d} onChange={change} testPrefix="duty" />
          </Panel>
          <Panel title="3 · Trip Log (Time & KM)"><FieldGrid fields={trip} data={d} onChange={change} cols={4} testPrefix="duty" /></Panel>
          <Panel title="4 · Client Billing"><FieldGrid fields={billing} data={d} onChange={change} testPrefix="duty" /></Panel>
          {admin && <Panel title={market ? "5 · Owner Payout" : "5 · Own Vehicle Expenses"}>
            <p className="text-xs text-muted-foreground mb-4">
              {market ? "Owner payable = owner package + extra KM/hr at owner rates + allowances + toll/parking/state tax." : "Toll, parking and state tax entered above are counted as expenses for own vehicles."}
            </p>
            <FieldGrid fields={cost} data={d} onChange={change} cols={market ? 4 : 3} testPrefix="duty" />
          </Panel>}
          <Panel title="6 · Remarks"><FieldGrid fields={[{ key: "remarks", label: "Remarks / Notes", type: "textarea", span: 3 }]} data={d} onChange={change} testPrefix="duty" /></Panel>
        </div>

        <aside className="xl:sticky xl:top-24 rounded-xl border bg-slate-950 text-slate-100 p-5 shadow-lg grain" data-testid="duty-summary">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-amber-400">Live Calculation</div>
            <StatusBadge value={d.status} />
          </div>
          <div className="[&_span.text-muted-foreground]:text-slate-400 divide-y divide-white/10">
            <div className="pb-2">
              <Row l="Total KM" v={`${c.totalKm.toFixed(1)} km`} testid="calc-total-km" />
              <Row l="Total Hours" v={`${c.totalHours.toFixed(2)} hrs`} testid="calc-total-hours" />
              <Row l="Extra KM" v={`${c.extraKm.toFixed(1)} km`} testid="calc-extra-km" />
              <Row l="Extra Hours" v={`${c.extraHours.toFixed(2)} hrs`} testid="calc-extra-hours" />
            </div>
            <div className="py-2">
              <Row l="Toll / Parking / Tax" v={inr(c.pass)} />
              <Row l="Client Billing" v={inr(c.billing, 2)} strong testid="calc-billing" />
              {admin && <Row l={market ? "Owner Payable" : "Total Expenses"} v={inr(c.cost, 2)} testid="calc-cost" />}
              {admin && market && <Row l="Owner Balance" v={inr(Math.max(0, c.cost - (parseFloat(d.owner_paid) || 0)), 2)} testid="calc-owner-balance" />}
            </div>
            {admin && <div className="pt-3">
              <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-slate-400">{market ? "Commission / Profit" : "Net Profit"}</div>
              <div className={`font-heading text-4xl font-bold num mt-1 ${c.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="calc-profit">{inr(c.profit, 2)}</div>
              {c.billing > 0 && <div className="text-xs text-slate-400 mt-1">Margin {((c.profit / c.billing) * 100).toFixed(1)}%</div>}
            </div>}
          </div>
          <Button onClick={save} disabled={saving} className="w-full mt-5 h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="duty-save-btn-summary">
            {saving ? "Saving..." : "Save Duty"}
          </Button>
        </aside>
      </div>
      <div className="h-24 xl:hidden" />
      <div className="xl:hidden fixed inset-x-0 bottom-0 z-30 bg-slate-950 text-white border-t border-white/10 px-3 pt-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] flex items-center gap-3" data-testid="duty-mobile-bar">
        <div className="min-w-0 flex-1 grid grid-cols-2 gap-2">
          <div className="min-w-0"><div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Billing</div><div className="num font-bold truncate" data-testid="mobile-calc-billing">{inr(c.billing)}</div></div>
          {admin && <div className="min-w-0"><div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold">Profit</div><div className={`num font-bold truncate ${c.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="mobile-calc-profit">{inr(c.profit)}</div></div>}
        </div>
        <Button onClick={save} disabled={saving} className="h-12 px-5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold shrink-0" data-testid="duty-save-btn-mobile">
          <Save className="h-4 w-4 mr-1.5" />{saving ? "Saving" : "Save Duty"}
        </Button>
      </div>
    </div>
  );
}
